import { SchedulePersonnel, TimeOffRange } from '../interfaces/Schedule';
import { UnavailableDaysEntry } from '../interfaces/UnavailableDays';

const calendarDay = (date: Date): Date =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate());

// Count unique calendar days inclusively, regardless of time of day or DST.
export const countUnavailableDays = (ranges: TimeOffRange[]): number => {
  const dayNumber = (date: Date) =>
    Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()) / 86400000;
  const days = ranges
    .map((range) => ({
      start: dayNumber(range.startAt),
      end: dayNumber(range.endAt),
    }))
    .filter(
      ({ start, end }) =>
        Number.isFinite(start) && Number.isFinite(end) && start <= end,
    )
    .sort((a, b) => a.start - b.start);
  let total = 0;
  let lastEnd = -Infinity;
  days.forEach(({ start, end }) => {
    total += Math.max(0, end - Math.max(start, lastEnd + 1) + 1);
    lastEnd = Math.max(lastEnd, end);
  });
  return total;
};

// Preserve existing leave; combine overlaps only when the leave types agree.
export const mergeUnavailableDays = (
  personnels: SchedulePersonnel[],
  entries: UnavailableDaysEntry[],
): { personnels: SchedulePersonnel[]; conflicts: string[] } => {
  const conflicts: string[] = [];
  const mergedPersonnels = personnels.map((personnel) => {
    const additions = entries.filter(
      (entry) => entry.personnelID === personnel.ID,
    );
    if (!additions.length) return personnel;

    const sorted = [...personnel.unavailableDays, ...additions]
      .map((range) => ({
        startAt: calendarDay(range.startAt),
        endAt: calendarDay(range.endAt),
        ptoSubtype: range.ptoSubtype,
      }))
      .sort((a, b) => a.startAt.getTime() - b.startAt.getTime());
    const ranges: TimeOffRange[] = [];
    sorted.forEach((range) => {
      const previous = ranges[ranges.length - 1];
      if (previous && range.startAt <= previous.endAt) {
        if (previous.ptoSubtype !== range.ptoSubtype) {
          if (!conflicts.includes(personnel.name)) {
            conflicts.push(personnel.name);
          }
        } else if (range.endAt > previous.endAt) {
          previous.endAt = range.endAt;
        }
      } else {
        ranges.push({
          startAt: range.startAt,
          endAt: range.endAt,
          ptoSubtype: range.ptoSubtype,
        });
      }
    });
    return { ...personnel, unavailableDays: ranges };
  });
  // A conflict must never produce a partially applied result.
  return {
    personnels: conflicts.length ? personnels : mergedPersonnels,
    conflicts,
  };
};
